import { params, platformAccess } from '@app/configurations';
import { GlobalFilterDefaults } from '@app/core/store/states/global-filters/global-filters-state.models';
import { getPlatformLogo, getPlatformStatusLogo } from '@app/core/utilities/account.utilities';
import { extractAccountRefiners } from '@app/core/utilities/refiners.utilities';
import { Capitalize, snakeToCamelCase, WrapInParenthesis } from '@app/core/utilities/string.utilities';
import { environment } from '@env/environment';
import { FilterMode } from '../models/selector.model';

export function isMonitor() {
  return location.pathname.indexOf('monitor') !== -1;
}

export function GetCampaignsStatus(campaignStatus) {
  return { ACTIVE: 'success', PAUSED: 'warning', DELETED: 'danger' }[campaignStatus] || 'danger';
}

export function CampaignsDTO(d) {
  return {
    id: d.adspertCampaignId,
    name: d.campaignName,
    campaignName: d.campaignName,
    campaignType: d.campaignType,
    accountName: d.accountName,
    accountId: d.accountId,
    performanceGroupName: d.performanceGroupName,
    performanceGroupOptimizationType: d.performanceGroupOptimizationType,
    type: d.campaignStatus,
    logo: getPlatformStatusLogo(d.accountPlatform, d.campaignStatus),
    accountPlatform: d.accountPlatform,
    status: GetCampaignsStatus(d.campaignStatus),
  };
}

export function AdGroupsDTO(d) {
  return {
    id: d.adspertAdgroupId,
    name: d.adgroupName,
    adgroupName: d.adgroupName,
    accountPlatform: d.accountPlatform,
    accountName: d.accountName,
    accountId: d.accountId,
    campaignName: d.campaignName,
    campaignType: d.campaignType,
    type: d.adgroupStatus,
    logo: getPlatformStatusLogo(d.accountPlatform, d.adgroupStatus),
    status: { ACTIVE: 'success', PAUSED: 'warning', DELETED: 'danger' }[d.adgroupStatus] || 'danger',
  };
}

export function CriteriaDTO(d) {
  return {
    id: d.criterionId,
    name: d.criterionName,
    criterionName: d.criterionName,
    adgroupName: d.adgroupName,
    adspertAdgroupId: d.adspertAdgroupId,
    accountName: d.accountName,
    accountPlatform: d.accountPlatform,
    accountId: d.accountId,
    campaignName: d.campaignName,
    campaignType: d.campaignType,
    type: d.criterionStatus,
    logo: getPlatformStatusLogo(d.accountPlatform, d.criterionStatus),
    status: { ACTIVE: 'success', PAUSED: 'warning', DELETED: 'danger' }[d.criterionStatus] || 'danger',
  };
}
function getPlatformStatus(status, optimize) {
  switch (status) {
    case platformAccess.OK.key:
      return optimize ? 'GREEN' : 'YELLOW';
    case platformAccess.READ_ONLY.key:
      return optimize ? 'YELLOW' : 'RED';
    default:
      return 'RED';
  }
}
export function AccountDTO(d) {
  return {
    id: d.adspertAccountId,
    name: d.accountName,
    accountId: d.accountId,
    customerId: d.customerId,
    signupCompleted: d.signupCompleted,
    tags: d.tagsUser,
    logo: !d.signupCompleted ? `${environment.app.cdnURL}assets/spinner.png` : getPlatformLogo(d.platform),
    platform: d.platform,
    optimize: d.optimize,
    platformAccessStatus: d.platformAccessStatus,
    status: getPlatformStatus(d.platformAccessStatus, d.optimize),
  };
}

export function PGsDTO(d) {
  return {
    id: d.performanceGroupId,
    name: d.name,
    type: d.optimizationType,
    accountName: d.accountName,
  };
}

export function getAccountFilters(filterDefaults: GlobalFilterDefaults, state: any, filter = []) {
  if (typeof state[params.ADSPERT_ID] !== 'string' && typeof state[params.GLOBAL_FILTER_ACCOUNT_QUERY] !== 'string') {
    filter.push(...extractAccountRefiners(filterDefaults.accountsDefaultFilter).map((e) => [e[0], e[1]]));
  } else {
    if (state[params.ADSPERT_ID]) filter.push(['filter_adspert_account_id', state[params.ADSPERT_ID]]);
    else if (state[params.GLOBAL_FILTER_ACCOUNT_QUERY])
      filter.push(...extractAccountRefiners(state[params.GLOBAL_FILTER_ACCOUNT_QUERY]).map((e) => [e[0], e[1]]));
  }
  return filter;
}

export function getPGFilters(selectedFilters: [String, String][] | null, hideInactive: boolean, filterDefaults, state) {
  const filter: any = getAccountFilters(filterDefaults, state);
  if (hideInactive) filter.push(['filter_account_optimize', true]);

  if (selectedFilters?.length)
    filter.push(...selectedFilters.filter((d) => !(hideInactive && d[0] === 'filter_account_optimize'))); // if there are criteria filter subsets

  if (isMonitor() && state[params.MONITOR_OPTI_TYPE])
    filter.push(['filter_campaign_optimization_type', state[params.MONITOR_OPTI_TYPE].toUpperCase()]);

  return ConvertCase(filter);
}

export function getCampaignFilters(
  selectedFilters: [String, String][] | null,
  filterDefaults,
  state,
  forDestination = false,
) {
  const filter: any = getAccountFilters(filterDefaults, state);

  const paramMapper = {
    pgID: !forDestination ? params.GLOBAL_FILTER_PERFORMANCE_GROUP_ID : params.GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_ID,
  };

  const pg = state[paramMapper.pgID];
  if (!!pg) filter.push(['filter_performance_group_id', pg]);

  if (selectedFilters?.length) filter.push(...selectedFilters);

  if (isMonitor() && state[params.MONITOR_OPTI_TYPE])
    filter.push(['filter_campaign_optimization_type', state[params.MONITOR_OPTI_TYPE].toUpperCase()]);

  return ConvertCase(filter);
}

export function getAdGroupFilters(
  selectedFilters: [String, String][] | null,
  filterDefaults,
  state,
  forDestination = false,
) {
  const filter: any = getAccountFilters(filterDefaults, state);

  const paramMapper = {
    pgID: !forDestination ? params.GLOBAL_FILTER_PERFORMANCE_GROUP_ID : params.GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_ID,
    campaignID: !forDestination ? params.GLOBAL_FILTER_CAMPAIGN_ID : params.GLOBAL_FILTER_DEST_CAMPAIGN_ID,
  };

  const pg = state[paramMapper.pgID];
  if (!!pg) filter.push(['filter_performance_group_id', pg]);

  const campaign = state[paramMapper.campaignID];
  if (!!campaign) filter.push(['filter_adspert_campaign_id', campaign]);

  if (selectedFilters?.length) filter.push(...selectedFilters);

  return ConvertCase(filter);
}

export function getCriteriaFilters(selectedFilters: [String, String][] | null, filterDefaults, state) {
  const filter: any = [];

  const paramMapper = {
    pgID: params.GLOBAL_FILTER_PERFORMANCE_GROUP_ID,
    campaignID: params.GLOBAL_FILTER_CAMPAIGN_ID,
    adID: params.GLOBAL_FILTER_AD_GROUPS_ID,
  };

  const pg = state[paramMapper.pgID];
  if (!!pg) filter.push(['filter_performance_group_id', pg]);

  const campaign = state[paramMapper.campaignID];
  if (!!campaign) filter.push(['filter_adspert_campaign_id', campaign]);

  const adGrp = state[paramMapper.adID];
  if (!!adGrp) filter.push(['filter_adspert_adgroup_id', adGrp]);

  if (selectedFilters?.length) filter.push(...selectedFilters);

  return ConvertCase(filter);
}

export function getSelectorTooltip(dt, hasDest, customFilterTexts = {}) {
  let tooltip = '';
  const data = [dt[0]];
  if (hasDest) data.push(dt[1]);

  if (data.filter(({ type }) => type === 'LABEL').length !== 2) {
    tooltip = data
      .map((item, i) => {
        const value: string[] = !hasDest
          ? []
          : [i === 0 ? Capitalize(FilterMode.Source) : Capitalize(FilterMode.Destination), ': '];

        if (item.type === 'FILTER') {
          let isCustomFilter = false;
          Object.keys(customFilterTexts).forEach((key) => {
            if (key === item.payload || (key === 'EMPTY_STRING' && item.payload.join() === '')) {
              value.push(customFilterTexts[key]);
              isCustomFilter = true;
            }
          });
          if (!isCustomFilter) value.push(item.payload.join(', '));
        } else if (item.type === 'SELECTED_ITEM') value.push(item.payload.name);
        else return '';

        return value.join('').trim();
      })
      .join('\n\n');
  }
  return tooltip?.trim();
}

export function getButtonLabel(dt, hasDest, customFilterTexts = {}) {
  const data = [dt[0]];
  const pre = [hasDest ? `${Capitalize(FilterMode.Source)}: ` : ''];
  if (hasDest) {
    data.push(dt[1]);
    pre.push(`${Capitalize(FilterMode.Destination)}: `);
  }

  return data.map((item, i) => {
    if (item.type === 'FILTER') {
      let isCustomFilter = false;
      let value;
      Object.keys(customFilterTexts).forEach((key) => {
        if (key === item.payload || (key === 'EMPTY_STRING' && item.payload.join() === '')) {
          value = pre[i] + customFilterTexts[key];
          isCustomFilter = true;
        }
      });
      if (!isCustomFilter) return pre[i] + 'Filters ' + WrapInParenthesis(item.payload.length);
      else return value;
    } else if (item.type === 'SELECTED_ITEM') return pre[i] + item.payload.name;
  });
}

export function ConvertCase(data, q = 'filter_') {
  return data.reduce((acc, [key, value]) => {
    acc[snakeToCamelCase(key.replace(q, ''))] = value;
    return acc;
  }, {});
}
